import React from 'react';
import { BookOpen, ArrowLeft } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { useNavigate } from 'react-router-dom';
import useSmartBack from './useSmartBack';

const isMyBookieDomain = () => {
  const hostname = window.location.hostname;
  return !hostname.includes('repti-sitter') && !hostname.includes('reptisitter');
};

const getHomePath = () => isMyBookieDomain() ? '/' : '/mybookie';

const MyBookieTerms = () => {
  const navigate = useNavigate();
  const goBack = useSmartBack(getHomePath());
  
  return (
    <div className="min-h-screen bg-[#F6F5F2]">
      {/* Header */}
      <nav className="bg-white border-b border-[#E6E8E6] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div 
            className="flex items-center gap-3 cursor-pointer" 
            onClick={() => navigate(getHomePath())}
          >
            <div className="w-10 h-10 bg-[#0A2B1D] rounded-sm flex items-center justify-center">
              <BookOpen className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-semibold text-[#111614] tracking-tight">MyBookie</span>
          </div>
          <Button 
            variant="ghost" 
            onClick={goBack}
            className="text-[#5C6661] hover:text-[#111614]"
            data-testid="terms-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        <div className="bg-white border border-[#E6E8E6] p-8 md:p-12">
          <h1 className="text-3xl font-semibold text-[#111614] mb-2">Terms of Service</h1>
          <p className="text-[#5C6661] mb-8">Last updated: February 2026</p>

          <div className="prose prose-gray max-w-none">
            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">1. Acceptance of Terms</h2>
              <p className="text-[#5C6661] leading-relaxed mb-4">
                Welcome to MyBookie. By accessing or using our bookkeeping and expense tracking service ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service.
              </p>
              <p className="text-[#5C6661] leading-relaxed">
                These Terms constitute a legally binding agreement between you and MyBookie. We reserve the right to modify these Terms at any time, and such modifications will be effective immediately upon posting.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">2. Description of Service</h2>
              <p className="text-[#5C6661] leading-relaxed mb-4">
                MyBookie provides a cloud-based bookkeeping and financial management platform that enables users to:
              </p>
              <ul className="list-disc list-inside text-[#5C6661] space-y-2">
                <li>Track business expenses and income</li>
                <li>Connect bank accounts to automatically import transactions</li>
                <li>Categorize and organize financial data</li>
                <li>Generate financial reports and summaries</li>
                <li>Forecast cash flow based on recurring transactions</li>
                <li>Access AI-powered financial insights</li>
                <li>Export data and share reports with accountants</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">3. Account Registration</h2>
              <p className="text-[#5C6661] leading-relaxed mb-4">
                To use MyBookie, you must create an account. You agree to:
              </p>
              <ul className="list-disc list-inside text-[#5C6661] space-y-2">
                <li>Provide accurate, current, and complete information</li>
                <li>Maintain and promptly update your account information</li>
                <li>Keep your password secure and confidential</li>
                <li>Be responsible for all activity under your account</li>
                <li>Notify us immediately of any unauthorized use</li>
              </ul>
              <p className="text-[#5C6661] leading-relaxed mt-4">
                You must be at least 18 years old to create an account and use the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">4. Subscription and Payment</h2>
              
              <h3 className="text-lg font-medium text-[#111614] mb-3">4.1 Free Trial</h3>
              <p className="text-[#5C6661] leading-relaxed mb-4">
                We may offer a free trial period. After the trial expires, you will need to subscribe to continue using the Service.
              </p>

              <h3 className="text-lg font-medium text-[#111614] mb-3">4.2 Billing</h3>
              <p className="text-[#5C6661] leading-relaxed mb-4">
                Subscription fees are billed in advance on a monthly or annual basis. All fees are non-refundable except as expressly stated in these Terms or required by law.
              </p>

              <h3 className="text-lg font-medium text-[#111614] mb-3">4.3 Price Changes</h3>
              <p className="text-[#5C6661] leading-relaxed">
                We reserve the right to modify pricing. Price changes will be communicated in advance and will take effect at the start of your next billing cycle.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">5. Bank Account Connections</h2>
              <p className="text-[#5C6661] leading-relaxed mb-4">
                MyBookie uses Plaid Inc. to facilitate bank account connections. By connecting your bank account, you:
              </p>
              <ul className="list-disc list-inside text-[#5C6661] space-y-2">
                <li>Authorize Plaid to access your financial account information</li>
                <li>Agree to Plaid's terms of service and privacy policy</li>
                <li>Understand that we receive read-only access to your transaction data</li>
                <li>Acknowledge that bank data accuracy depends on your financial institution</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">6. Acceptable Use</h2>
              <p className="text-[#5C6661] leading-relaxed mb-4">You agree NOT to:</p>
              <ul className="list-disc list-inside text-[#5C6661] space-y-2">
                <li>Use the Service for any illegal purpose</li>
                <li>Attempt to gain unauthorized access to any part of the Service</li>
                <li>Interfere with or disrupt the Service or servers</li>
                <li>Upload malicious code, viruses, or harmful content</li>
                <li>Impersonate any person or entity</li>
                <li>Use the Service to transmit spam or unsolicited messages</li>
                <li>Reverse engineer or attempt to extract source code</li>
                <li>Resell or redistribute the Service without authorization</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">7. Your Data</h2>
              
              <h3 className="text-lg font-medium text-[#111614] mb-3">7.1 Ownership</h3>
              <p className="text-[#5C6661] leading-relaxed mb-4">
                You retain all ownership rights to your data. By using MyBookie, you grant us a limited license to use, process, and store your data solely to provide the Service.
              </p>

              <h3 className="text-lg font-medium text-[#111614] mb-3">7.2 Data Export</h3>
              <p className="text-[#5C6661] leading-relaxed mb-4">
                You may export your data at any time using the export features provided in the Service.
              </p>

              <h3 className="text-lg font-medium text-[#111614] mb-3">7.3 Data Deletion</h3>
              <p className="text-[#5C6661] leading-relaxed">
                Upon account termination, your data will be deleted in accordance with our Privacy Policy, subject to any legal retention requirements.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">8. Intellectual Property</h2>
              <p className="text-[#5C6661] leading-relaxed">
                The Service, including its design, features, content, and underlying technology, is owned by MyBookie and protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, distribute, or create derivative works without our written permission.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">9. Disclaimer of Warranties</h2>
              <p className="text-[#5C6661] leading-relaxed mb-4">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT:
              </p>
              <ul className="list-disc list-inside text-[#5C6661] space-y-2">
                <li>The Service will be uninterrupted or error-free</li>
                <li>The Service will meet your specific requirements</li>
                <li>Financial data imported will be 100% accurate</li>
                <li>The AI-powered features will provide accurate financial advice</li>
              </ul>
              <p className="text-[#5C6661] leading-relaxed mt-4 font-medium">
                MyBookie is a bookkeeping tool, not a substitute for professional accounting, tax, or financial advice. Always consult qualified professionals for financial decisions.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">10. Limitation of Liability</h2>
              <p className="text-[#5C6661] leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, MYBOOKIE SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING LOSS OF PROFITS, DATA, OR BUSINESS OPPORTUNITIES, ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT EXCEED THE AMOUNT PAID BY YOU IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">11. Indemnification</h2>
              <p className="text-[#5C6661] leading-relaxed">
                You agree to indemnify and hold harmless MyBookie, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including reasonable attorneys' fees) arising from your use of the Service or violation of these Terms.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">12. Termination</h2>
              <p className="text-[#5C6661] leading-relaxed mb-4">
                You may cancel your account at any time. We may suspend or terminate your account if you violate these Terms or for any other reason at our discretion.
              </p>
              <p className="text-[#5C6661] leading-relaxed">
                Upon termination, your right to use the Service will cease immediately. Sections relating to intellectual property, disclaimers, limitations of liability, and indemnification shall survive termination.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">13. Governing Law</h2>
              <p className="text-[#5C6661] leading-relaxed">
                These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles. Any disputes shall be resolved in the courts of competent jurisdiction.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-medium text-[#111614] mb-4">14. Changes to Terms</h2>
              <p className="text-[#5C6661] leading-relaxed">
                We reserve the right to modify these Terms at any time. Material changes will be communicated via email or through the Service. Your continued use after changes constitutes acceptance of the updated Terms.
              </p>
            </section>

            <section>
              <h2 className="text-xl font-medium text-[#111614] mb-4">15. Contact Us</h2>
              <p className="text-[#5C6661] leading-relaxed">
                If you have questions about these Terms, please contact us at:
              </p>
              <p className="text-[#0A2B1D] mt-4">
                <strong>Email:</strong> legal@mybookie.ai
              </p>
            </section>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#111614] text-[#8A948F] py-8 px-6">
        <div className="max-w-4xl mx-auto text-center text-sm">
          © 2026 MyBookie. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default MyBookieTerms;
